import { useState } from "react";
import { base44 } from "@/api/base44Client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Plus, Search, Building2, Star, Download, Upload, ArrowLeft,
  ChevronRight, Target, Users, Wallet, LayoutGrid, Columns3,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { cn } from "@/lib/utils";
import AcquisitionCandidateForm from "@/components/acquisitions/AcquisitionCandidateForm";
import ImportCandidatesDialog from "@/components/acquisitions/ImportCandidatesDialog";
import CandidatePipeline from "@/components/acquisitions/CandidatePipeline";
import { jsPDF } from "jspdf";
import { toast } from "sonner";

const statusColors = {
  prospect: "bg-slate-100 text-slate-700",
  initial_contact: "bg-sky-100 text-sky-700",
  interested: "bg-cyan-100 text-cyan-700",
  info_sent: "bg-brand-limeSoft text-brand-limeDark",
  evaluation: "bg-brand-limeSoft text-brand-limeDark",
  letter_of_intent: "bg-amber-100 text-amber-700",
  negotiation_dd: "bg-orange-100 text-orange-700",
  closed_won: "bg-emerald-100 text-emerald-700",
};
const statusLabels = {
  prospect: "Prospekt",
  initial_contact: "Innledende kontakt",
  interested: "Noe interessert",
  info_sent: "Sendt info",
  evaluation: "Evaluering",
  letter_of_intent: "Intensjonsavtale",
  negotiation_dd: "Forhandling/DD",
  closed_won: "Gjennomført",
};
const statusOrder = ["all", "prospect", "initial_contact", "interested", "info_sent", "evaluation", "letter_of_intent", "negotiation_dd", "closed_won"];

const fmtM = (n) => `${((n || 0) / 1e6).toFixed(1)}M kr`;

export default function AcquisitionCandidates() {
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [customerSearch, setCustomerSearch] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [editingCandidate, setEditingCandidate] = useState(null);
  const [viewMode, setViewMode] = useState("pipeline");
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: candidates = [] } = useQuery({
    queryKey: ["acquisition-candidates"],
    queryFn: () => base44.entities.AcquisitionCandidate.list("-created_date"),
  });
  const { data: customers = [] } = useQuery({
    queryKey: ["customers"],
    queryFn: () => base44.entities.BusinessCustomer.list(),
  });

  const createCandidateMutation = useMutation({
    mutationFn: (data) => base44.entities.AcquisitionCandidate.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["acquisition-candidates"] });
      setShowForm(false);
      setEditingCandidate(null);
      toast.success("Kandidat lagret");
    },
  });
  const updateCandidateMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.AcquisitionCandidate.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["acquisition-candidates"] });
      setShowForm(false);
      setEditingCandidate(null);
      toast.success("Kandidat oppdatert");
    },
  });
  const importMutation = useMutation({
    mutationFn: (list) => base44.entities.AcquisitionCandidate.bulkCreate(list),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ["acquisition-candidates"] });
      setShowImport(false);
      toast.success(`Importerte ${created.length} kandidater`);
    },
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, status }) => base44.entities.AcquisitionCandidate.update(id, { status }),
    onMutate: async ({ id, status }) => {
      await queryClient.cancelQueries({ queryKey: ["acquisition-candidates"] });
      const prev = queryClient.getQueryData(["acquisition-candidates"]);
      queryClient.setQueryData(["acquisition-candidates"], (old = []) =>
        old.map((c) => (c.id === id ? { ...c, status } : c))
      );
      return { prev };
    },
    onError: (e, vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(["acquisition-candidates"], ctx.prev);
      toast.error("Kunne ikke flytte kandidat");
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["acquisition-candidates"] }),
  });

  const selectedCustomer = customers.find((c) => c.id === selectedCustomerId) || null;

  const handleSubmit = (data) => {
    const withCustomer = selectedCustomer
      ? { ...data, customer_id: selectedCustomer.id, customer_name: selectedCustomer.company_name }
      : data;
    if (editingCandidate) updateCandidateMutation.mutate({ id: editingCandidate.id, data: withCustomer });
    else createCandidateMutation.mutate(withCustomer);
  };

  const handleImport = (rows) => {
    const enriched = rows.map((r) => ({
      ...r,
      customer_id: selectedCustomer?.id || null,
      customer_name: selectedCustomer?.company_name || null,
    }));
    importMutation.mutate(enriched);
  };

  const countFor = (customerId) => candidates.filter((c) => c.customer_id === customerId).length;
  const pipelineFor = (customerId) =>
    candidates.filter((c) => c.customer_id === customerId).reduce((s, c) => s + (c.discussed_price || 0), 0);

  const getScoreColor = (score) => (score >= 80 ? "text-emerald-600" : score >= 60 ? "text-amber-600" : "text-rose-600");

  /* ---------------------------- CUSTOMER PICKER ---------------------------- */
  if (!selectedCustomerId) {
    const filteredCustomers = customers
      .filter((c) => c.company_name.toLowerCase().includes(customerSearch.toLowerCase()))
      .sort((a, b) => countFor(b.id) - countFor(a.id) || a.company_name.localeCompare(b.company_name));

    const totalPipeline = candidates.reduce((s, c) => s + (c.discussed_price || 0), 0);
    const activeCustomerCount = customers.filter((c) => countFor(c.id) > 0).length;

    return (
      <div className="min-h-screen bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-8 flex items-center gap-4 animate-fade-in">
            <div className="w-12 h-12 rounded-2xl bg-brand-ink text-brand-lime flex items-center justify-center shrink-0">
              <Target className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-3xl font-extrabold text-brand-ink tracking-tight">Oppkjøpskandidater</h1>
              <p className="text-slate-500 mt-0.5">Velg kunden dere jobber for for å se kandidatene</p>
            </div>
          </div>

          {/* Summary KPIs */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            {[
              { label: "Kunder med kandidater", value: activeCustomerCount, icon: Users, chip: "bg-brand-ink text-brand-lime" },
              { label: "Totalt kandidater", value: candidates.length, icon: Target, chip: "bg-brand-limeSoft text-brand-limeDark" },
              { label: "Samlet pipeline", value: fmtM(totalPipeline), icon: Wallet, chip: "bg-emerald-50 text-emerald-600" },
            ].map((s, i) => (
              <div key={i} className="rounded-2xl bg-white border border-slate-200/70 shadow-sm p-5 flex items-center justify-between animate-fade-in" style={{ animationDelay: `${i * 60}ms` }}>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{s.label}</p>
                  <p className="mt-2 text-2xl font-extrabold text-brand-ink">{s.value}</p>
                </div>
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${s.chip}`}>
                  <s.icon className="w-5 h-5" />
                </div>
              </div>
            ))}
          </div>

          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
            <Input placeholder="Søk etter kunde..." value={customerSearch} onChange={(e) => setCustomerSearch(e.target.value)} className="pl-10 h-11 bg-white" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filteredCustomers.map((customer, i) => {
              const count = countFor(customer.id);
              const pipeline = pipelineFor(customer.id);
              return (
                <button
                  key={customer.id}
                  onClick={() => { setSelectedCustomerId(customer.id); setStatusFilter("all"); setSearchTerm(""); }}
                  className="group text-left rounded-2xl bg-white border border-slate-200/70 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 p-6 animate-fade-in"
                  style={{ animationDelay: `${i * 40}ms` }}
                >
                  <div className="flex items-start justify-between">
                    <div className="w-12 h-12 rounded-xl bg-brand-limeSoft flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-brand-limeDark" />
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-brand-ink transition-colors" />
                  </div>
                  <h3 className="mt-4 font-semibold text-brand-ink text-lg leading-tight">{customer.company_name}</h3>
                  <p className="text-sm text-slate-400">{customer.contact_name}</p>
                  <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
                    <div>
                      <p className="text-2xl font-extrabold text-brand-ink leading-none">{count}</p>
                      <p className="text-xs text-slate-400 mt-1">{count === 1 ? "kandidat" : "kandidater"}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-brand-ink">{fmtM(pipeline)}</p>
                      <p className="text-xs text-slate-400 mt-1">pipeline</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {showForm && (
          <AcquisitionCandidateForm
            candidate={editingCandidate}
            onSubmit={handleSubmit}
            onClose={() => { setShowForm(false); setEditingCandidate(null); }}
            isLoading={createCandidateMutation.isPending || updateCandidateMutation.isPending}
          />
        )}
      </div>
    );
  }

  /* ---------------------------- CANDIDATES VIEW ---------------------------- */
  const customerCandidates = candidates.filter((c) => c.customer_id === selectedCustomerId);
  const filteredCandidates = customerCandidates.filter((c) => {
    const matchesSearch =
      c.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.contact_person?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || c.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const statusCounts = statusOrder.reduce((acc, s) => {
    acc[s] = s === "all" ? customerCandidates.length : customerCandidates.filter((c) => c.status === s).length;
    return acc;
  }, {});

  const pipeline = customerCandidates.reduce((s, c) => s + (c.discussed_price || 0), 0);
  const avgScore = customerCandidates.length
    ? Math.round(customerCandidates.reduce((s, c) => s + (c.overall_score || 0), 0) / customerCandidates.length)
    : 0;

  const handleExportToPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text(`Oppkjøpskandidater – ${selectedCustomer?.company_name || ""}`, 20, 20);
    doc.setFontSize(10);
    doc.text(`Generert: ${new Date().toLocaleDateString("nb-NO")}`, 20, 28);
    doc.text(`Antall kandidater: ${filteredCandidates.length}`, 20, 34);
    let y = 46;
    filteredCandidates.forEach((c, index) => {
      if (y > 270) { doc.addPage(); y = 20; }
      doc.setFontSize(12); doc.setFont(undefined, "bold");
      doc.text(`${index + 1}. ${c.company_name}`, 20, y); y += 6;
      doc.setFontSize(9); doc.setFont(undefined, "normal");
      if (c.contact_person) { doc.text(`Kontakt: ${c.contact_person}`, 25, y); y += 5; }
      doc.text(`Status: ${statusLabels[c.status] || c.status}`, 25, y); y += 5;
      if (c.revenue) { doc.text(`Omsetning: ${c.revenue.toLocaleString("nb-NO")} kr`, 25, y); y += 5; }
      if (c.discussed_price) { doc.text(`Pris: ${c.discussed_price.toLocaleString("nb-NO")} kr`, 25, y); y += 5; }
      if (c.overall_score) { doc.text(`Score: ${c.overall_score}/100`, 25, y); y += 5; }
      y += 4;
    });
    doc.save(`kandidater_${(selectedCustomer?.company_name || "kunde").replace(/\s+/g, "_")}.pdf`);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button
          onClick={() => setSelectedCustomerId(null)}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-brand-ink transition-colors mb-4"
        >
          <ArrowLeft className="w-4 h-4" /> Alle kunder
        </button>

        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6 animate-fade-in">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-brand-limeSoft flex items-center justify-center shrink-0">
              <Building2 className="w-6 h-6 text-brand-limeDark" />
            </div>
            <div>
              <h1 className="text-2xl font-extrabold text-brand-ink tracking-tight">{selectedCustomer?.company_name}</h1>
              <p className="text-slate-500 text-sm">Oppkjøpskandidater for denne kunden</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => setShowImport(true)} variant="outline">
              <Upload className="w-4 h-4 mr-2" /> Importer Excel/CSV
            </Button>
            <Button onClick={handleExportToPDF} variant="outline">
              <Download className="w-4 h-4 mr-2" /> Eksporter PDF
            </Button>
            <Button onClick={() => setShowForm(true)}>
              <Plus className="w-4 h-4 mr-2" /> Ny kandidat
            </Button>
          </div>
        </div>

        {/* Mini stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          {[
            { label: "Kandidater", value: customerCandidates.length },
            { label: "Pipeline", value: fmtM(pipeline) },
            { label: "Snitt score", value: customerCandidates.length ? `${avgScore}/100` : "—" },
          ].map((s, i) => (
            <div key={i} className="rounded-2xl bg-white border border-slate-200/70 shadow-sm p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{s.label}</p>
              <p className="mt-1.5 text-xl font-extrabold text-brand-ink">{s.value}</p>
            </div>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
            <Input placeholder="Søk etter bedrift eller kontakt..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 h-11 bg-white" />
          </div>
          <div className="flex items-center gap-1 p-1 rounded-xl bg-white border border-slate-200 shrink-0 self-start">
            <button
              onClick={() => setViewMode("pipeline")}
              className={cn("flex items-center gap-1.5 px-3 h-9 rounded-lg text-sm font-medium transition-colors", viewMode === "pipeline" ? "bg-brand-ink text-white" : "text-slate-500 hover:text-brand-ink")}
            >
              <Columns3 className="w-4 h-4" /> Pipeline
            </button>
            <button
              onClick={() => setViewMode("cards")}
              className={cn("flex items-center gap-1.5 px-3 h-9 rounded-lg text-sm font-medium transition-colors", viewMode === "cards" ? "bg-brand-ink text-white" : "text-slate-500 hover:text-brand-ink")}
            >
              <LayoutGrid className="w-4 h-4" /> Kort
            </button>
          </div>
        </div>

        {viewMode === "cards" && (
          <div className="flex gap-2 overflow-x-auto pb-2 mb-6">
            {statusOrder.map((s) => (
              <Button
                key={s}
                variant={statusFilter === s ? "default" : "outline"}
                size="sm"
                onClick={() => setStatusFilter(s)}
                className="shrink-0 bg-white data-[active=true]:bg-brand-ink"
                data-active={statusFilter === s}
              >
                {s === "all" ? "Alle" : statusLabels[s]} ({statusCounts[s]})
              </Button>
            ))}
          </div>
        )}

        {customerCandidates.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center border border-slate-200/70 shadow-sm">
            <div className="w-16 h-16 rounded-2xl bg-brand-limeSoft flex items-center justify-center mx-auto mb-4">
              <Target className="w-8 h-8 text-brand-limeDark" />
            </div>
            <h3 className="text-lg font-semibold text-brand-ink mb-2">Ingen kandidater her ennå</h3>
            <p className="text-slate-500 mb-6">Legg til manuelt eller importer en liste fra Excel/CSV</p>
            <div className="flex justify-center gap-2">
              <Button onClick={() => setShowImport(true)} variant="outline"><Upload className="w-4 h-4 mr-2" /> Importer</Button>
              <Button onClick={() => setShowForm(true)}><Plus className="w-4 h-4 mr-2" /> Ny kandidat</Button>
            </div>
          </div>
        ) : viewMode === "pipeline" ? (
          <CandidatePipeline
            candidates={customerCandidates.filter((c) =>
              c.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
              c.contact_person?.toLowerCase().includes(searchTerm.toLowerCase())
            )}
            onStatusChange={(id, status) => statusMutation.mutate({ id, status })}
            onOpen={(c) => navigate(createPageUrl("AcquisitionCandidateDetail") + "?id=" + c.id)}
          />
        ) : filteredCandidates.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center border border-slate-200/70 shadow-sm">
            <p className="text-slate-500">Ingen kandidater matcher filteret.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filteredCandidates.map((candidate, i) => (
              <div
                key={candidate.id}
                onClick={() => navigate(createPageUrl("AcquisitionCandidateDetail") + "?id=" + candidate.id)}
                className="group bg-white rounded-2xl p-6 border border-slate-200/70 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 cursor-pointer animate-fade-in"
                style={{ animationDelay: `${i * 40}ms` }}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start gap-3 flex-1">
                    <div className="w-11 h-11 rounded-xl bg-brand-limeSoft flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-brand-limeDark" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-brand-ink leading-tight">{candidate.company_name}</h3>
                      <p className="text-sm text-slate-400">{candidate.contact_person}</p>
                    </div>
                  </div>
                  {candidate.overall_score ? (
                    <div className="flex items-center gap-1">
                      <Star className={cn("w-4 h-4", getScoreColor(candidate.overall_score))} />
                      <span className={cn("text-sm font-semibold", getScoreColor(candidate.overall_score))}>{candidate.overall_score}</span>
                    </div>
                  ) : null}
                </div>

                <Badge className={cn("mb-4 border-0", statusColors[candidate.status])}>
                  {statusLabels[candidate.status] || candidate.status}
                </Badge>

                <div className="space-y-2 text-sm">
                  {candidate.revenue ? (
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">Omsetning</span>
                      <span className="font-semibold text-brand-ink">{candidate.revenue.toLocaleString("nb-NO")} kr</span>
                    </div>
                  ) : null}
                  {candidate.discussed_price ? (
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">Pris diskutert</span>
                      <span className="font-semibold text-brand-ink">{candidate.discussed_price.toLocaleString("nb-NO")} kr</span>
                    </div>
                  ) : null}
                  {candidate.ebitda ? (
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">EBITDA</span>
                      <span className="font-semibold text-emerald-600">{candidate.ebitda.toLocaleString("nb-NO")} kr</span>
                    </div>
                  ) : null}
                  {candidate.multiple ? (
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">Multipel</span>
                      <span className="font-semibold text-brand-ink">{candidate.multiple}x</span>
                    </div>
                  ) : null}
                </div>

                {candidate.industry && (
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <span className="text-xs text-slate-400">{candidate.industry}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {showForm && (
        <AcquisitionCandidateForm
          candidate={editingCandidate}
          defaultCustomerId={selectedCustomerId}
          onSubmit={handleSubmit}
          onClose={() => { setShowForm(false); setEditingCandidate(null); }}
          isLoading={createCandidateMutation.isPending || updateCandidateMutation.isPending}
        />
      )}

      <ImportCandidatesDialog
        open={showImport}
        onOpenChange={setShowImport}
        customer={selectedCustomer}
        onImport={handleImport}
        isImporting={importMutation.isPending}
      />
    </div>
  );
}
